// «Речь» — pure helpers for transcript navigation, chunk planning and result merging.
// No Angular here; transcript.service.ts wires them to signals.
import type { Highlight } from '../models/twitch.model';
import type { TranscriptLine } from '../models/transcript.model';
import { overlapsRegion, type TimeRegion } from './region.js';
import { isChatBot } from './chat-bots.js';

/** Index of the last line whose start ≤ t (binary search), or -1 before the first line. */
export function lineIndexAt(starts: Float64Array, t: number): number {
  let lo = 0;
  let hi = starts.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid] <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

export interface TranscriptChunk {
  fromLine: number;   // inclusive
  toLine: number;     // exclusive
  startSec: number;
  endSec: number;
}

export interface ChunkPlanOptions {
  targetSec?: number;   // window length the plan aims for
  slackSec?: number;    // a cut may move this far from its target to land on a pause
  maxChars?: number;    // ≈ formatted chars per window (more windows if the speech is dense)
}

// Windows do NOT overlap: the server shows the model a few minutes around each one as
// read-only context and keeps only segments that START inside the window (each moment has
// exactly one owner — no halves or copies at the seams). The model finds about the same number
// of moments per request whatever the length, so size decides recall (measured 2026-10-22):
// 2 h windows found ~15% fewer moments, 3 h ~30% fewer, the whole stream at once 3-4× fewer —
// and 90-min windows PLUS the server's 15 min of context also lost ~20% on a 10.9 h stream
// (recrent) vs the old 90-min overlapping chunks. 75 min + context ≈ the old 90 min of text.
const DEFAULT_PLAN: Required<ChunkPlanOptions> = { targetSec: 75 * 60, slackSec: 10 * 60, maxChars: 90000 };
const LINE_OVERHEAD_CHARS = 10; // "[1234] " prefix + newline + amortized "# 1:23:00" minute markers

function charsOf(lines: TranscriptLine[], from: number, to: number): number {
  let c = 0;
  for (let i = from; i < to; i++) c += lines[i][2].length + LINE_OVERHEAD_CHARS;
  return c;
}

/**
 * Equal-length windows over lines [from, to), each cut moved to the longest speech pause
 * within ±slack of its target (a pause is where a topic usually ends). Absolute line indices.
 */
export function planCores(lines: TranscriptLine[], opts: ChunkPlanOptions = {}, from = 0, to = lines.length): TranscriptChunk[] {
  const o = { ...DEFAULT_PLAN, ...opts };
  if (to <= from) return [];
  const t0 = lines[from][0];
  const total = lines[to - 1][1] - t0;
  const k = Math.max(1, Math.round(total / o.targetSec), Math.ceil(charsOf(lines, from, to) / o.maxChars));
  const bounds = [from];
  for (let j = 1; j < k; j++) {
    const target = t0 + (total * j) / k;
    const prev = bounds[bounds.length - 1];
    let best = -1;
    let bestScore = -Infinity;
    for (let i = prev + 1; i < to; i++) {
      const s = lines[i][0];
      if (s < target - o.slackSec) continue;
      if (s > target + o.slackSec) break;
      // Longest pause wins; a minute away from the target costs one second of pause.
      const score = (s - lines[i - 1][1]) - Math.abs(s - target) / 60;
      if (score > bestScore) { bestScore = score; best = i; }
    }
    if (best < 0) {
      // No speech within ±slack (a long silence) — cut at the first line after the target.
      best = prev + 1;
      while (best < to && lines[best][0] < target) best++;
    }
    if (best >= to) break;
    bounds.push(best);
  }
  bounds.push(to);
  const out: TranscriptChunk[] = [];
  for (let j = 0; j + 1 < bounds.length; j++) {
    out.push({ fromLine: bounds[j], toLine: bounds[j + 1], startSec: lines[bounds[j]][0], endSec: lines[bounds[j + 1] - 1][1] });
  }
  return out;
}

/** Line range [from, to) of the lines that start inside [startSec, endSec) — «область интереса». */
export function lineRangeIn(lines: TranscriptLine[], startSec: number, endSec: number): [number, number] {
  let from = 0;
  while (from < lines.length && lines[from][0] < startSec) from++;
  let to = from;
  while (to < lines.length && lines[to][0] < endSec) to++;
  return [from, to];
}

/**
 * planCores over the «область интереса». Line indices stay absolute: the server validates
 * fromLine/toLine against the whole transcript it loads from GCS.
 */
export function planCoresInRange(lines: TranscriptLine[], startSec: number, endSec: number, opts: ChunkPlanOptions = {}): TranscriptChunk[] {
  const [from, to] = lineRangeIn(lines, startSec, endSec);
  return planCores(lines, opts, from, to);
}

// AI search goes out as ONE request over the whole stream / region: measured 2026-10-22,
// a long stretch across a 90-min seam came back whole in 97% vs 51% with windows, short
// moments were found as well, and it's ~2× faster. Only a transcript past this size
// (≈ a day of talk) is split, into windows of up to this many chars each.
const SEARCH_SINGLE_MAX_CHARS = 450000;
const SEARCH_WINDOW_CHARS = 300000;

export function planSearch(lines: TranscriptLine[], from = 0, to = lines.length): TranscriptChunk[] {
  if (to <= from) return [];
  if (charsOf(lines, from, to) <= SEARCH_SINGLE_MAX_CHARS) {
    return [{ fromLine: from, toLine: to, startSec: lines[from][0], endSec: lines[to - 1][1] }];
  }
  return planCores(lines, { targetSec: 1e9, maxChars: SEARCH_WINDOW_CHARS }, from, to);
}

// --- Chat for «+ чат» runs: [t, text] pairs, no nicknames, `!commands` and chat bots' messages
// dropped (timers / ads, like AI Deep — utils/chat-bots.ts). A dense minute is thinned EVENLY (not
// its first N: the reaction to a moment comes at the end of the minute), and the true per-minute
// counts go along — the server's `· 💬 N` and 🔥 spike marker are computed from them, not from the
// thinned texts.
const CHAT_TEXT_MAX = 200;
const CHAT_PER_MINUTE_SEND = 20;

export interface ChatPayload {
  chat: Array<[number, string]>;
  chatCounts: Array<[number, number]>;   // [minute, messages in it before thinning]
}

export function chatPayload(
  comments: ReadonlyArray<{ content_offset_seconds: number; message: { body: string }; commenter?: { display_name?: string } }>,
  startSec: number,
  endSec: number,
): ChatPayload {
  const kept: Array<[number, string]> = [];
  const total = new Map<number, number>();
  for (const c of comments) {
    const t = c.content_offset_seconds;
    if (!(t >= startSec && t <= endSec)) continue;
    const text = (c.message?.body || '').replace(/\s+/g, ' ').trim();
    if (!text || text.startsWith('!') || isChatBot(c.commenter?.display_name)) continue;
    const tr = Math.round(t * 10) / 10;
    kept.push([tr, text.slice(0, CHAT_TEXT_MAX)]);
    const m = Math.floor(tr / 60);
    total.set(m, (total.get(m) || 0) + 1);
  }
  const seen = new Map<number, number>();
  const chat = kept.filter(([t]) => {
    const m = Math.floor(t / 60);
    const n = total.get(m)!;
    const i = (seen.get(m) || 0) + 1;
    seen.set(m, i);
    return n <= CHAT_PER_MINUTE_SEND
      || Math.floor((i * CHAT_PER_MINUTE_SEND) / n) !== Math.floor(((i - 1) * CHAT_PER_MINUTE_SEND) / n);
  });
  return { chat, chatCounts: [...total.entries()] };
}

/** Twitch categories of the VOD → the server's `context.categories` ([start, end, name]). */
export function categoriesPayload(chapters: ReadonlyArray<{ startSec: number; endSec: number; name: string }>): Array<[number, number, string]> {
  return chapters.slice(0, 50).map(c => [Math.round(c.startSec), Math.round(c.endSec), c.name]);
}

/** Intersection over the SHORTER span — 1 means one span sits inside the other. */
export function overlapRatio(a: { startTime: number; endTime: number }, b: { startTime: number; endTime: number }): number {
  const inter = Math.min(a.endTime, b.endTime) - Math.max(a.startTime, b.startTime);
  if (inter <= 0) return 0;
  const shorter = Math.max(1e-6, Math.min(a.endTime - a.startTime, b.endTime - b.startTime));
  return inter / shorter;
}

/**
 * Greedy de-dup of overlapping candidates (chunk overlaps produce near-duplicates):
 * best score first, longer first on ties (the copy not cut by a window edge), and a
 * candidate is dropped if it overlaps an accepted one by ≥ threshold.
 */
export function dedupeByOverlap<T extends { startTime: number; endTime: number }>(
  items: T[],
  score: (item: T) => number,
  threshold = 0.5,
): T[] {
  const sorted = [...items].sort((a, b) =>
    (score(b) - score(a)) || ((b.endTime - b.startTime) - (a.endTime - a.startTime)));
  const accepted: T[] = [];
  for (const it of sorted) {
    if (accepted.every(a => overlapRatio(a, it) < threshold)) accepted.push(it);
  }
  return accepted;
}

const isBinKey = (t: number) => Math.abs(t / 15 - Math.round(t / 15)) < 1e-9;

/**
 * Highlights are identified by startTime alone (selectHighlight / pin / delete / caches),
 * so a transcript segment must never share one with another highlight — nor sit on a
 * 15s multiple, which is where future peak clusters get their keys.
 */
export function uniqueStartTime(t: number, taken: Set<number>): number {
  let x = Math.round(t * 10) / 10;
  while (taken.has(x) || isBinKey(x)) x = Math.round((x + 0.1) * 10) / 10;
  return x;
}

/**
 * Replace previous results of `source` with `incoming`. Kept from previous runs: every other
 * source, plus same-source items the user touched (pinned / promoted / trashed — a trashed
 * segment must not resurrect on re-run). With `scope` (a run limited to the «область
 * интереса»), same-source items outside it are kept too — re-running segments in one part
 * of the stream must not wipe the ones found elsewhere. Incoming items overlapping a kept
 * same-source item by ≥ 0.5 are dropped. Result sorted by startTime (the order highlights[]
 * is kept in).
 */
export function mergeTranscriptHighlights(
  existing: Highlight[],
  incoming: Highlight[],
  source: 'transcript-ai' | 'transcript-search',
  scope: TimeRegion | null = null,
): Highlight[] {
  const retained = existing.filter(h =>
    h.source !== source || h.isPinned || h.isPromoted || h.isDeleted
    || (!!scope && !overlapsRegion(h.startTime, h.endTime, scope)));
  const keptSame = retained.filter(h => h.source === source);
  const taken = new Set(retained.map(h => h.startTime));
  const fresh: Highlight[] = [];
  for (const nh of incoming) {
    if (keptSame.some(r => overlapRatio(r, nh) >= 0.5)) continue;
    const startTime = uniqueStartTime(nh.startTime, taken);
    taken.add(startTime);
    fresh.push({ ...nh, startTime });
  }
  return [...retained, ...fresh].sort((a, b) => a.startTime - b.startTime);
}
